import React, { useState, useCallback, useEffect } from 'react';
import { Video } from 'lucide-react';
import { getCuisineIcon } from '@/utils/iconMaps';
import {
  Sheet,
  SheetContent,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MultiVideoUploader, VideoEntry } from './MultiVideoUploader';
import { VideoEditorModal } from './VideoEditorModal';
import { DishLinkStep } from '@/components/submission/DishLinkStep';
import { ReviewMatrix, ReviewRatings } from '@/components/submission/ReviewMatrix';
import { ChipSelector } from '@/components/ui/chip-selector';
import { SubmissionFlow } from '@/components/SubmissionFlow';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, Link2, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PostToEdit {
  id: string;
  content: string | null;
  video_url: string | null;
  video_urls?: string[] | null;
  dish_id?: string | null;
  rating_value?: string | null;
  rating_portion?: string | null;
  rating_taste?: string | null;
  tags?: string[] | null;
  dishes?: { id: string; name: string } | null;
}

interface CreateDroolSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialLinkedDish?: { id: string; name: string; restaurantName?: string; imageUrl?: string | null; cuisine?: string[] | null } | null;
  initialPost?: PostToEdit | null;
}

const makeId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const MAX_DROOL_DURATION = 30; // seconds

export const CreateDroolSheet: React.FC<CreateDroolSheetProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialLinkedDish,
  initialPost,
}) => {
  const isEditMode = !!initialPost;
  const { user } = useAuth();

  // Videos — keyed by stable ID
  const [videos, setVideos] = useState<VideoEntry[]>([]);

  // Form fields
  const [serviceType, setServiceType] = useState<'dine_in' | 'takeaway' | null>(null);
  const [linkedDish, setLinkedDish] = useState<{ id: string; name: string; restaurantName?: string; imageUrl?: string | null; cuisine?: string[] | null } | null>(initialLinkedDish ?? null);
  const [ratings, setRatings] = useState<ReviewRatings>({
    ratingValue: null,
    ratingPortion: null,
    ratingTaste: null,
  });
  const [content, setContent] = useState('');
  const [showRatings, setShowRatings] = useState(false);

  // Dietary & allergen
  const [dietaryClaims, setDietaryClaims] = useState<{ id: string; name: string; icon: string | null }[]>([]);
  const [allergens, setAllergens] = useState<{ id: string; name: string; icon: string | null }[]>([]);
  const [selectedDietaryIds, setSelectedDietaryIds] = useState<string[]>([]);
  const [selectedAllergenIds, setSelectedAllergenIds] = useState<string[]>([]);
  const [showDietary, setShowDietary] = useState(false);
  const [showAllergens, setShowAllergens] = useState(false);

  // Video editor
  const [editingVideoId, setEditingVideoId] = useState<string | null>(null);
  const [pendingTrimFile, setPendingTrimFile] = useState<{ file: File; localUrl: string } | null>(null);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmissionFlow, setShowSubmissionFlow] = useState(false);
  const [pendingRestaurantData, setPendingRestaurantData] = useState<{
    name: string;
    place_id: string;
    address?: string;
    lat?: number;
    lng?: number;
  } | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (initialPost) {
        const existingUrls = initialPost.video_urls?.length
          ? initialPost.video_urls
          : initialPost.video_url ? [initialPost.video_url] : [];
        setVideos(existingUrls.map((url, i) => ({
          id: `existing-${i}-${Date.now()}`,
          localUrl: url,
          uploadedUrl: url,
          isUploading: false,
        })));
        setContent(initialPost.content ?? '');
        if (initialPost.rating_value || initialPost.rating_portion || initialPost.rating_taste) {
          setRatings({
            ratingValue: (initialPost.rating_value ?? null) as ReviewRatings['ratingValue'],
            ratingPortion: (initialPost.rating_portion ?? null) as ReviewRatings['ratingPortion'],
            ratingTaste: (initialPost.rating_taste ?? null) as ReviewRatings['ratingTaste'],
          });
          setShowRatings(true);
        }
        if (initialPost.dishes) {
          setLinkedDish({ id: initialPost.dishes.id, name: initialPost.dishes.name });
        }
        const dietaryIds = (initialPost.tags ?? []).filter((t) => t.startsWith('dietary:')).map((t) => t.replace('dietary:', ''));
        const allergenIds = (initialPost.tags ?? []).filter((t) => t.startsWith('allergen:')).map((t) => t.replace('allergen:', ''));
        setSelectedDietaryIds(dietaryIds);
        setSelectedAllergenIds(allergenIds);
        if (dietaryIds.length > 0) setShowDietary(true);
        if (allergenIds.length > 0) setShowAllergens(true);
      } else {
        setLinkedDish(initialLinkedDish ?? null);
      }
      supabase.from('dietary_claims').select('id, name, icon').then(({ data }) => {
        if (data) setDietaryClaims(data);
      });
      supabase.from('allergens').select('id, name, icon').then(({ data }) => {
        if (data) setAllergens(data);
      });
    }
  }, [isOpen]);

  const resetForm = useCallback(() => {
    setEditingVideoId(null);
    setPendingTrimFile(null);
    setVideos([]);
    setServiceType(null);
    setLinkedDish(initialLinkedDish ?? null);
    setRatings({ ratingValue: null, ratingPortion: null, ratingTaste: null });
    setContent('');
    setShowRatings(false);
    setSelectedDietaryIds([]);
    setSelectedAllergenIds([]);
    setShowDietary(false);
    setShowAllergens(false);
    setIsSubmitting(false);
    setShowSubmissionFlow(false);
    setPendingRestaurantData(null);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  // ---------------------------------------------------------------------------
  // Video upload — uses stable video ID, immune to array reordering/deletion
  // ---------------------------------------------------------------------------
  const uploadVideoById = async (file: File, videoId: string) => {
    if (!user) return;

    try {
      const fileExt = file.name.split('.').pop() || 'mp4';
      const fileName = `${user.id}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${fileExt}`;

      const { data, error } = await supabase.storage
        .from('post-videos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type || 'video/mp4',
        });

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('post-videos')
        .getPublicUrl(data.path);

      setVideos((prev) =>
        prev.map((v) =>
          v.id === videoId ? { ...v, uploadedUrl: publicUrl, isUploading: false } : v
        )
      );
    } catch (err) {
      console.error('[CreateDroolSheet] Video upload failed:', err);
      toast.error('Failed to upload video. Please try again.');
      setVideos((prev) => prev.filter((v) => v.id !== videoId));
    }
  };

  const handleAddVideo = useCallback(
    (file: File) => {
      if (!user) return;
      // Always open the trim editor — re-encoding guarantees the upload stays within size limits
      setPendingTrimFile({ file, localUrl: URL.createObjectURL(file) });
    },
    [user]
  );

  const handleDeleteVideo = useCallback((id: string) => {
    setVideos((prev) => prev.filter((v) => v.id !== id));
  }, []);

  const handleEditorSave = useCallback(
    async (blob: Blob, previewUrl: string) => {
      const targetId = editingVideoId;
      setEditingVideoId(null);
      if (!targetId || !user) return;

      // Optimistically update the preview and mark as re-uploading
      setVideos((prev) =>
        prev.map((v) =>
          v.id === targetId ? { ...v, localUrl: previewUrl, uploadedUrl: '', isUploading: true, status: 'uploading' } : v
        )
      );

      // Re-upload the processed blob
      try {
        const ext = (blob.type || '').includes('mp4') ? 'mp4' : 'webm';
        const file = new File([blob], `edited-video.${ext}`, { type: blob.type || 'video/webm' });
        const fileName = `${user.id}/${Date.now()}_edited_${Math.random().toString(36).slice(2, 8)}.${ext}`;

        const { data, error } = await supabase.storage
          .from('post-videos')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false,
            contentType: file.type || 'video/mp4',
          });

        if (error) throw error;

        const { data: { publicUrl } } = supabase.storage
          .from('post-videos')
          .getPublicUrl(data.path);

        setVideos((prev) =>
          prev.map((v) =>
            v.id === targetId ? { ...v, uploadedUrl: publicUrl, isUploading: false } : v
          )
        );
      } catch (err) {
        console.error('[CreateDroolSheet] Edited video upload failed:', err);
        toast.error('Failed to save edited video.');
        setVideos((prev) =>
          prev.map((v) => v.id === targetId ? { ...v, isUploading: false } : v)
        );
      }
    },
    [editingVideoId, user]
  );

  const handlePendingTrimSave = useCallback(
    async (blob: Blob, previewUrl: string) => {
      const pending = pendingTrimFile;
      setPendingTrimFile(null);
      if (!pending || !user) return;

      const id = makeId();
      const ext = blob.type.includes('mp4') ? 'mp4' : 'webm';
      const file = new File([blob], `drool.${ext}`, { type: blob.type || 'video/webm' });

      setVideos((prev) => [...prev, { id, localUrl: previewUrl, uploadedUrl: '', isUploading: true, status: 'uploading' }]);
      uploadVideoById(file, id);
    },
    [pendingTrimFile, user]
  );

  // ---------------------------------------------------------------------------
  // Dish link
  // ---------------------------------------------------------------------------
  const handleDishSelect = useCallback(
    (dishId: string, dishName: string, restaurantName?: string, imageUrl?: string | null, cuisine?: string[] | null) => {
      setLinkedDish({ id: dishId, name: dishName, restaurantName, imageUrl, cuisine });
    },
    []
  );

  const handleAddNewDish = useCallback(
    (restaurantData: { name: string; place_id: string; address?: string; lat?: number; lng?: number }) => {
      setPendingRestaurantData(restaurantData);
      setShowSubmissionFlow(true);
    },
    []
  );

  const handleDishCreated = useCallback((dishId: string, dishName: string) => {
    setLinkedDish({ id: dishId, name: dishName });
    setShowSubmissionFlow(false);
    setPendingRestaurantData(null);
  }, []);

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------
  const allUploaded = videos.length > 0 && videos.every((v) => !v.isUploading && v.uploadedUrl);
  const canSubmit = allUploaded && !!linkedDish && !isSubmitting;

  const handleSubmit = async () => {
    if (!user) { toast.error('Please sign in to post a drool'); return; }
    if (videos.length === 0) { toast.error('Please add at least one video'); return; }
    if (!allUploaded) { toast.error('Please wait for videos to finish uploading'); return; }
    if (!linkedDish) { toast.error('Please link a dish before posting'); return; }

    const uploadedUrls = videos.map((v) => v.uploadedUrl);
    const tags: string[] = [
      ...selectedDietaryIds.map((id) => `dietary:${id}`),
      ...selectedAllergenIds.map((id) => `allergen:${id}`),
    ];

    setIsSubmitting(true);
    try {
      if (isEditMode && initialPost) {
        const { error } = await supabase
          .from('posts')
          .update({
            content: content.trim() || null,
            video_url: uploadedUrls[0],
            video_urls: uploadedUrls,
            dish_id: linkedDish.id,
            rating_value: ratings.ratingValue || null,
            rating_portion: ratings.ratingPortion || null,
            rating_taste: ratings.ratingTaste || null,
            tags: tags.length > 0 ? tags : null,
          } as any)
          .eq('id', initialPost.id)
          .eq('user_id', user.id);

        if (error) throw error;
        toast.success('Drool updated!');
      } else {
        const { error } = await supabase
          .from('posts')
          .insert({
            user_id: user.id,
            content: content.trim() || null,
            image_url: null,
            video_url: uploadedUrls[0],
            video_urls: uploadedUrls,
            dish_id: linkedDish.id,
            media_type: 'video',
            rating_value: ratings.ratingValue || null,
            rating_portion: ratings.ratingPortion || null,
            rating_taste: ratings.ratingTaste || null,
            tags: tags.length > 0 ? tags : null,
          } as any);

        if (error) throw error;
        toast.success('Drool posted!');
      }

      resetForm();
      onSuccess();
    } catch (err) {
      console.error('[CreateDroolSheet] Submit failed:', err);
      toast.error(`Failed to ${isEditMode ? 'update' : 'post'} drool. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <SheetContent
          side="bottom"
          className="h-[96vh] flex flex-col p-0 rounded-t-2xl border-t border-border [&>button]:hidden"
        >
          {/* Header */}
          <div className="flex-shrink-0 px-4 pt-4 pb-4 border-b border-border">
            <div className="flex items-center justify-between">
              <div className="w-16" />
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Video className="h-5 w-5" />
                {isEditMode ? 'Edit Drool' : 'Post a Drool'}
              </h2>
              <div className="w-16 flex justify-end">
                <Button variant="outline" size="sm" onClick={handleClose}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>

          {/* Scrollable Body */}
          <div className="flex-1 overflow-y-auto px-4 py-5 space-y-7">

            {/* Videos */}
            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                Videos
              </h3>
              <MultiVideoUploader
                videos={videos}
                onAdd={handleAddVideo}
                onDelete={handleDeleteVideo}
                onEdit={setEditingVideoId}
                maxVideos={3}
              />
            </section>

            {/* Service Type */}
            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                Where did you eat?
              </h3>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setServiceType('dine_in')}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 transition-all text-sm font-medium',
                    serviceType === 'dine_in'
                      ? 'border-[#cfef00] bg-[#cfef00]/10 text-foreground'
                      : 'border-border bg-background text-muted-foreground hover:border-[#cfef00]/50'
                  )}
                >
                  <span>🍽️</span>
                  <div className="text-left">
                    <p className="font-medium">Dine In</p>
                    <p className="text-xs font-normal opacity-70">Ate at the restaurant</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setServiceType('takeaway')}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 transition-all text-sm font-medium',
                    serviceType === 'takeaway'
                      ? 'border-[#cfef00] bg-[#cfef00]/10 text-foreground'
                      : 'border-border bg-background text-muted-foreground hover:border-[#cfef00]/50'
                  )}
                >
                  <span>🥡</span>
                  <div className="text-left">
                    <p className="font-medium">Takeaway</p>
                    <p className="text-xs font-normal opacity-70">Ordered to go</p>
                  </div>
                </button>
              </div>
            </section>

            {/* Dish Link */}
            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                Link a Dish <span className="text-xs font-normal normal-case tracking-normal text-muted-foreground">(Required)</span>
              </h3>
              {linkedDish ? (
                <div className="flex items-center justify-between p-3 bg-[#cfef00]/10 border border-[#cfef00]/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    {linkedDish.imageUrl ? (
                      <img
                        src={linkedDish.imageUrl}
                        alt={linkedDish.name}
                        className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-[#cfef00]/20 flex items-center justify-center flex-shrink-0">
                        <Link2 className="h-5 w-5 text-[#cfef00]" />
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-foreground">{linkedDish.name}</p>
                      {linkedDish.restaurantName && (
                        <p className="text-xs text-muted-foreground">{linkedDish.restaurantName}</p>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setLinkedDish(null)}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <DishLinkStep
                  onSelect={handleDishSelect}
                  onBack={() => {}}
                  showBack={false}
                  onAddNewDish={handleAddNewDish}
                  serviceType={serviceType}
                />
              )}
            </section>

            {/* Cuisine Type (auto-filled from linked dish) */}
            {linkedDish?.cuisine && linkedDish.cuisine.length > 0 && (
              <section className="space-y-2">
                <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Cuisine Type</h3>
                <div className="flex flex-wrap gap-2">
                  {linkedDish.cuisine.map((c) => (
                    <div key={c} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted">
                      <span className="text-lg">{getCuisineIcon(c)}</span>
                      <span className="text-sm font-medium capitalize">{c.replace('_', ' ')}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Ratings (collapsible) */}
            <section className="space-y-2">
              <button
                type="button"
                onClick={() => setShowRatings((v) => !v)}
                className="flex items-center justify-between w-full group"
              >
                <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                  Rate It
                  <span className="ml-2 text-xs font-normal text-muted-foreground normal-case tracking-normal">
                    (Optional)
                  </span>
                </h3>
                {showRatings
                  ? <ChevronUp className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  : <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                }
              </button>
              {showRatings && (
                <ReviewMatrix
                  value={ratings}
                  onChange={(key, val) => setRatings((prev) => ({ ...prev, [key]: val }))}
                />
              )}
            </section>

            {/* Dietary */}
            <section className="space-y-2">
              <button
                type="button"
                onClick={() => setShowDietary((v) => !v)}
                className="flex items-center justify-between w-full group"
              >
                <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                  Dietary
                  <span className="ml-2 text-xs font-normal text-muted-foreground normal-case tracking-normal">
                    (Optional)
                  </span>
                </h3>
                {showDietary
                  ? <ChevronUp className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  : <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                }
              </button>
              {showDietary && dietaryClaims.length > 0 && (
                <ChipSelector
                  options={dietaryClaims.map((c) => ({ id: c.id, name: c.name, iconKey: c.icon }))}
                  selected={selectedDietaryIds}
                  onChange={setSelectedDietaryIds}
                  type="dietary"
                  colorScheme="green"
                />
              )}
            </section>

            {/* Allergens */}
            <section className="space-y-2">
              <button
                type="button"
                onClick={() => setShowAllergens((v) => !v)}
                className="flex items-center justify-between w-full group"
              >
                <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                  Allergens
                  <span className="ml-2 text-xs font-normal text-muted-foreground normal-case tracking-normal">
                    (Optional)
                  </span>
                </h3>
                {showAllergens
                  ? <ChevronUp className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  : <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                }
              </button>
              {showAllergens && allergens.length > 0 && (
                <ChipSelector
                  options={allergens.map((a) => ({ id: a.id, name: a.name, iconKey: a.icon }))}
                  selected={selectedAllergenIds}
                  onChange={setSelectedAllergenIds}
                  type="allergen"
                  colorScheme="amber"
                />
              )}
            </section>

            {/* Caption */}
            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                Caption
                <span className="ml-2 text-xs font-normal text-muted-foreground normal-case tracking-normal">
                  (Optional)
                </span>
              </h3>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="What made this drool-worthy?"
                className="min-h-[90px] resize-none"
                maxLength={500}
              />
              <div className="text-xs text-muted-foreground text-right">
                {content.length}/500
              </div>
            </section>

            <div className="h-2" />
          </div>

          {/* Sticky Submit */}
          <div className="flex-shrink-0 px-4 pb-6 pt-3 border-t border-border bg-background">
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="w-full h-12 text-base font-semibold bg-[#cfef00] hover:bg-[#cfef00]/90 text-black disabled:opacity-40"
            >
              {isSubmitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{isEditMode ? 'Saving...' : 'Posting...'}</>
              ) : (
                isEditMode ? 'Save Changes' : 'Share Drool'
              )}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Video editor */}
      {editingVideoId && (() => {
        const editingVideo = videos.find((v) => v.id === editingVideoId);
        return editingVideo ? (
          <VideoEditorModal
            isOpen
            videoSrc={editingVideo.localUrl}
            maxDuration={MAX_DROOL_DURATION}
            forceReencode
            onSave={handleEditorSave}
            onCancel={() => setEditingVideoId(null)}
          />
        ) : null;
      })()}

      {/* Trim editor for new videos longer than 30s */}
      {pendingTrimFile && (
        <VideoEditorModal
          isOpen
          videoSrc={pendingTrimFile.localUrl}
          maxDuration={MAX_DROOL_DURATION}
          forceReencode
          onSave={handlePendingTrimSave}
          onCancel={() => {
            URL.revokeObjectURL(pendingTrimFile.localUrl);
            setPendingTrimFile(null);
          }}
        />
      )}

      {/* New dish creation flow */}
      {showSubmissionFlow && pendingRestaurantData && (
        <SubmissionFlow
          isOpen
          onClose={() => { setShowSubmissionFlow(false); setPendingRestaurantData(null); }}
          mode="create"
          prefillData={{
            imageUrl: '',
            restaurantName: pendingRestaurantData.name,
            googlePlaceId: pendingRestaurantData.place_id,
            restaurantAddress: pendingRestaurantData.address,
            restaurantLat: pendingRestaurantData.lat,
            restaurantLng: pendingRestaurantData.lng,
            serviceType: serviceType || undefined,
            mediaType: 'video',
            skipPostCreation: true,
          }}
          onDishCreated={handleDishCreated}
        />
      )}
    </>
  );
};
